import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Avatar,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useManualBooking } from '../../../context/ManualBookingContext';
import { useAuth } from '../../../../context/AuthContext';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import GradientButton from '../../../../components/buttons/GradientButton';
import { getServiceStaffAssignment } from '../../../../shared/booking/service-staff/serviceStaffRouting';
import { employeeHasAvailabilityOnDate } from '../../../../shared/booking/employee-schedule/workingSchedule';
import { resolveEffectiveCompanyHours } from '../../../../locations/resolveLocationHours';

const StaffStep = ({ onNext, onBack }) => {
  const noAssignedStaffError =
    'No staff members are assigned to this service yet. Please go to Manage Services and add employees to this service.';
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyData } = useAuth();
  const { bookingData, updateBookingData } = useManualBooking();

  useEffect(() => {
    const fetchStaff = async () => {
      if (!companyData?.id) {
        setError('Company data is missing. Please ensure you are properly logged in.');
        setLoading(false);
        return;
      }

      if (!bookingData.selectedService?.id) {
        setError('Please select a service first.');
        setLoading(false);
        return;
      }

      if (!bookingData.date) {
        setError('Please select a date first.');
        setLoading(false);
        return;
      }

      try {
        const {
          staff: eligibleStaff,
          eligibleEmployeeIds,
        } = await getServiceStaffAssignment({
          companyData,
          serviceId: bookingData.selectedService.id,
          locationId: bookingData.locationId,
          location: bookingData.selectedLocation,
        });

        if (!eligibleStaff.length) {
          setError(noAssignedStaffError);
          return;
        }

        const totalDuration =
          (bookingData.selectedPackage?.duration
            ? parseInt(bookingData.selectedPackage.duration || 0, 10) + parseInt(bookingData.selectedPackage.bufferTime || 0, 10)
            : (bookingData.selectedService?.duration || 60) + (bookingData.selectedService?.bufferTime || 0)) +
          (bookingData.selectedAddons || []).reduce(
            (total, addon) => total + parseInt(addon.duration || 0, 10) + parseInt(addon.bufferTime || 0, 10),
            0
          );

        const validEmployees = eligibleStaff
          .filter((employee) => employee.id !== 'any')
          .filter((employee) => employeeHasAvailabilityOnDate({
            workingSchedule: employee.workingSchedule,
            date: new Date(bookingData.date),
            companyHours: resolveEffectiveCompanyHours({
              location: bookingData.selectedLocation,
              companyData,
              locationId: bookingData.locationId,
            }),
            totalDurationMinutes: totalDuration,
          }));
        
        if (validEmployees.length === 0) {
          setError('No assigned staff members are working on the selected date for this service. Please choose another date or update employee schedules.');
        } else {
          const allStaff = [
            {
              id: 'any',
              name: 'Any Available',
              displayName: 'Any Available',
              isAnyAvailable: true,
              photoURL: companyData.logoUrl || null,
            },
            ...validEmployees
          ];
          
          setStaff(allStaff);
          setError(null);
          updateBookingData({
            serviceEmployeeIds: eligibleEmployeeIds,
          });
        }
      } catch (err) {
        console.error('Error fetching staff:', err);
        setError('Failed to load staff members. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, [bookingData.date, bookingData.selectedAddons, bookingData.selectedService, companyData, noAssignedStaffError]);

  const handleSelectStaff = (staffMember) => {
    let updateData;

    if (staffMember.isAnyAvailable) {
      // Get all employees who can provide this service
      const availableEmployees = staff
        .filter(emp => !emp.isAnyAvailable)
        .map(emp => ({
          id: emp.id,
          name: emp.name,
          title: emp.title || 'Staff Member',
          isOwner: emp.isOwner || false,
          photoURL: emp.photoURL || null,
          workingSchedule: emp.workingSchedule || null,
        }));

      const defaultEmployee = availableEmployees[0];

      updateData = {
        selectedEmployee: {
          id: 'any',
          employeeId: defaultEmployee?.id || 'any',
          employeeIds: availableEmployees.map(emp => emp.id),
          availableEmployees,
          isAnyAvailable: true,
          displayName: 'Any Available',
          name: 'Any Available',
          employeeName: 'Any Available',
          employeeTitle: 'Staff Member',
          employeeIsOwner: false,
          actualEmployeeName: defaultEmployee?.name || null,
          actualEmployee: defaultEmployee || null,
          title: 'Staff Member'
        },
        employeeIds: availableEmployees.map(emp => emp.id),
        employeeId: defaultEmployee?.id || 'any',
        employeeName: 'Any Available',
        employeeTitle: 'Staff Member',
        employeeIsOwner: false
      };
    } else {
      updateData = {
        selectedEmployee: {
          id: staffMember.id,
          employeeId: staffMember.id,
          name: staffMember.name,
          employeeName: staffMember.name,
          employeeTitle: staffMember.title || 'Staff Member',
          employeeIsOwner: staffMember.isOwner || false,
          title: staffMember.title || 'Staff Member',
          isOwner: staffMember.isOwner || false,
          photoURL: staffMember.photoURL || null,
          workingSchedule: staffMember.workingSchedule || null,
        },
        employeeIds: [staffMember.id],
        employeeId: staffMember.id,
        employeeName: staffMember.name,
        employeeTitle: staffMember.title || 'Staff Member',
        employeeIsOwner: staffMember.isOwner || false,
      };
    }

    updateBookingData(updateData);
    onNext();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">{error}</Typography>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          {error === 'Please select a service first.' ? (
            <GradientButton 
              onClick={onBack} 
              variant="primary"
            >
              Go Back to Services
            </GradientButton>
          ) : error === 'Please select a date first.' ? (
            <GradientButton 
              onClick={onBack}
              variant="primary"
            >
              Go Back to Date
            </GradientButton>
          ) : error === noAssignedStaffError ? (
            <GradientButton 
              onClick={() =>
                navigate('/app/overview/settings?services')}
              variant="primary"
            >
              Go to Manage Services
            </GradientButton>
          ) : (
            <GradientButton 
              onClick={() => window.location.reload()} 
              variant="primary"
              startIcon={<RefreshIcon />}
            >
              Retry
            </GradientButton>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box>

      <Grid 
        container 
        spacing={4} 
        justifyContent="center"
        sx={{ px: 4 }}
      >
        {staff.map((staffMember) => (
          <Grid item xs={12} sm={6} md={3} key={staffMember.id}>
            <Box
              onClick={() => handleSelectStaff(staffMember)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
                px: 2,
              }}
            >
              <Avatar
                src={staffMember.photoURL}
                alt={staffMember.name}
                sx={{
                  width: 100,
                  height: 100,
                  mb: 2,
                  border: bookingData.selectedEmployee?.id === staffMember.id 
                    ? `3px solid ${theme.palette.primary.main}`
                    : 'none',
                }}
              />
              <Typography
                variant="subtitle1"
                align="center"
                sx={{
                  color: bookingData.selectedEmployee?.id === staffMember.id
                    ? 'primary.main'
                    : 'text.primary',
                  fontWeight: bookingData.selectedEmployee?.id === staffMember.id ? 600 : 400,
                }}
              >
                {staffMember.name}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <GradientButton 
          onClick={onBack}
          variant="primary"
        >
          Back
        </GradientButton>
      </Box>
    </Box>
  );
};

export default StaffStep; 